import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Card,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AddIcon from '@mui/icons-material/Add';
import ApiService from '../../../services/api';
import logger from '../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#eff6ff',
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '10px 20px',
  borderRadius: '10px',
  fontWeight: 600,
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  },
}));

const Holidays = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState('2025');
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await ApiService.get(`/admin/holiday/get-all-holidays?year=${year}`);
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];
        const normalized = list.map((h) => ({
          id: h.id,
          name: h.name,
          date: h.date,
          template: h.template,
          createdBy: h.createdBy?.full_name || h.userdetails?.full_name || '-',
          updatedBy: h.updatedBy?.full_name || '-',
        }));
        setHolidays(normalized);
      } catch (e) {
        logger.error('Failed to load holidays:', e?.response || e);
        setError(e?.response?.data?.message || 'Failed to load holidays');
      } finally {
        setLoading(false);
      }
    };
    fetchHolidays();
  }, [year]);

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <BeachAccessIcon fontSize="large" />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">Holidays</Typography>
            <Typography variant="body2" color="text.secondary">View and manage company holidays. Use the year filter or add a new holiday.</Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            value={year}
            onChange={handleYearChange}
            size="small"
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e5e7eb',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
              },
            }}
          >
            <MenuItem value="2025">2025</MenuItem>
            <MenuItem value="2024">2024</MenuItem>
          </Select>
          <AddButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin-dashboard/add-holiday')}
          >
            Add New Holiday
          </AddButton>
        </Box>
      </Header>


      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainerStyled component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#3b82f6' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading holidays...</Typography>
          </Box>
        ) : holidays.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <BeachAccessIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="text.secondary">No holidays found for {year}.</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <Th>Name</Th>
                <Th>Date</Th>
                <Th>Template</Th>
                <Th>Created By</Th>
                <Th>Updated By</Th>
                <Th align="center">Action</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map((holiday) => (
                <StyledTableRow key={holiday.id}>
                  <Td>
                    <Typography variant="subtitle2" fontWeight={600} color="#1f2937">
                      {holiday.name}
                    </Typography>
                  </Td>
                  <Td>{holiday.date}</Td>
                  <Td>{holiday.template}</Td>
                  <Td>{holiday.createdBy}</Td>
                  <Td>{holiday.updatedBy}</Td>
                  <Td align="center">
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        },
                      }}
                    >
                      Assign Holiday
                    </Button>
                  </Td>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainerStyled>
    </Container>
  );
};

export default Holidays;

